import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosHandler from "./axiosHandler";

// Helper function to decode JWT and extract roleId
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

export const signIn = createAsyncThunk(
  "user/signIn",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/login`, data);

      if (response) {
        const status = response?.data?.status;
        const message = response?.data?.message;
        const responseData = response?.data?.data;

        if (status === "mfa_required") {
          const payload = {
            mfaEnabled: true,
            message: message || "MFA verification required",
            secret: responseData?.secret,
            qrCodeUrl: responseData?.qrCodeUrl,
            manualEntryKey: responseData?.manualEntryKey,
            user: null,
            token: null,
            roleId: null,
          };
          return payload;
        }

        const token = responseData?.token;
        const user = responseData?.user;

        let roleId = responseData?.user?.userRoleId;
        if (!roleId && token) {
          const decodedToken = decodeJWT(token);
          roleId = decodedToken?.roleId;
        }

        const mfaEnabled = responseData?.mfaEnabled;

        const mfaSetupData = responseData?.mfaSetupResponse || {};
        const qrCodeUrl = mfaSetupData?.qrCodeUrl || responseData?.qrCodeUrl;
        const secretKey = mfaSetupData?.secret || responseData?.secretKey;
        const manualEntryKey =
          mfaSetupData?.manualEntryKey || responseData?.manualEntryKey;

        const mfaSetupRequired =
          mfaEnabled === true || !!(qrCodeUrl || secretKey);

        if (token) {
          localStorage.setItem("token", token);
          if (!mfaSetupRequired) {
            localStorage.setItem("user", JSON.stringify(user));
            localStorage.setItem("roleId", roleId);
          }
          axiosHandler.defaults.headers["Authorization"] = `Bearer ${token}`;
        }

        const payload = {
          user: user || null, // Return user data even for MFA users
          token: token || null, // Return token even for MFA users
          roleId: roleId || null, // Return roleId even for MFA users
          message: message || responseData?.message,
          mfaEnabled,
          mfaSetupResponse: responseData?.mfaSetupResponse, // Include full mfaSetupResponse
          qrCodeUrl,
          secretKey,
          manualEntryKey,
        };
        return payload;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const addNewUser = createAsyncThunk(
  "user/addNewUser",
  async (data, { rejectWithValue }) => {
    try {
      delete data["departmentId"];
      const response = await axiosHandler.post(`/user/addNewUser`, data);
      return response?.data;
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);
export const getManagerByDept = createAsyncThunk(
  "user/getManagerByDept",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/filterUser`, data);

      if (response) {
        return response?.data?.data;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);
export const filterUsersByDept_and_Role = createAsyncThunk(
  "user/filterUsersByDept_and_Role",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/filterUser`, data);
      if (response) {
        return response?.data?.data;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const userSignUp = createAsyncThunk(
  "user/userSignUp",
  async (data, { rejectWithValue }) => {
    try {
      delete data["departmentId"];
      const response = await axiosHandler.post(`/user/signup`, data);
      return response?.data;
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/update",
  async ({ userId, values }, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.put(`/user/update/${userId}`, values);

      return response?.data;
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const getUserById = createAsyncThunk(
  "user/getUserById",
  async (userId, { rejectWithValue }) => {
    try {
      // Add timestamp to prevent caching
      const response = await axiosHandler.get(
        `/user/get/${userId}?t=${Date.now()}`
      );

      if (response?.data?.data) {
        return response?.data?.data;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const getAllUsers = createAsyncThunk(
  "user/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.get(`/user/getAll`);
      return response?.data?.data;
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const getManagersByRoleId = createAsyncThunk(
  "user/getUserByRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/getUserByRole`, {
        roleId: roleId,
      });
      const managerList = response?.data?.data || [];
      return managerList;
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const deleteUserById = createAsyncThunk(
  "user/deleteUserById",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.delete(`/user/delete/${roleId}`);
      if (response?.status === 200) {
        return roleId;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const sendOTP = createAsyncThunk(
  "user/sendOTP",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/sendOtp`, email);
      if (response?.status === 200) {
        return;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/resetPassword",
  async (resetPasswordData, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(
        `/user/forgot-password`,
        resetPasswordData
      );
      if (response?.status === 200) {
        return;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

export const validateChangePassword = createAsyncThunk(
  "user/validateChangePassword",
  async ({ corporateEmail, tempPass }, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.get(
        `/user/validate-forgot-password`,
        {
          params: { corporateEmail, tempPass },
        }
      );

      if (response?.data?.status === "success" && response?.status === 200) {
        return response?.data?.status;
      } else {
        return rejectWithValue(response?.data?.status);
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

// MFA Setup - Get QR code and secret for MFA setup
export const mfaSetup = createAsyncThunk(
  "user/mfaSetup",
  async (corporateEmail, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(
        `/mfa/setup?email=${encodeURIComponent(corporateEmail)}`
      );

      if (response?.status === 200) {
        return response?.data?.data;
      }
    } catch (error) {
      console.error("mfaSetup API - Error:", error);
      return throwError(error, rejectWithValue);
    }
  }
);

// MFA Verify and Enable - Verify TOTP code and enable MFA for user
export const mfaVerifyAndEnable = createAsyncThunk(
  "user/mfaVerifyAndEnable",
  async ({ corporateEmail, token }, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/mfa/verify-and-enable`, {
        email: corporateEmail,
        totpCode: token,
      });

      if (response?.status === 200) {
        let user = response?.data?.data?.user;
        let authToken = response?.data?.data?.token;
        let roleId = response?.data?.data?.user?.userRoleId;

        if (!user || !authToken) {
          const storedToken = localStorage.getItem("token");
          if (storedToken) {
            authToken = storedToken;
            const decodedToken = decodeJWT(storedToken);
            if (decodedToken) {
              user = {
                id: decodedToken.id,
                firstName: decodedToken.firstName,
                lastName: decodedToken.lastName,
                corporateEmail: decodedToken.sub,
                userRoleId: decodedToken.roleId,
                mfaEnabled: true, // MFA is now enabled
              };
              roleId = decodedToken.roleId;
            }
          }
        }

        if (authToken && user && roleId) {
          localStorage.setItem("token", authToken);
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("roleId", roleId);
          axiosHandler.defaults.headers[
            "Authorization"
          ] = `Bearer ${authToken}`;
        }

        return {
          ...response?.data,
          data: {
            ...response?.data?.data,
            user: user,
            token: authToken,
          },
        };
      }
    } catch (error) {
      console.error("mfaVerifyAndEnable API - Error:", error);
      return throwError(error, rejectWithValue);
    }
  }
);

export const mfaLoginWithTotp = createAsyncThunk(
  "user/mfaLoginWithTotp",
  async ({ corporateEmail, password, totpCode }, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/login-with-mfa`, {
        corporateEmail: corporateEmail,
        password: password,
        totpCode: totpCode,
      });

      if (response?.status === 200) {
        const user = response?.data?.data?.user;
        const authToken = response?.data?.data?.token;
        const message = response?.data?.message;

        let roleId = response?.data?.data?.user?.userRoleId;
        if (!roleId && authToken) {
          const decodedToken = decodeJWT(authToken);
          roleId = decodedToken?.roleId;
        }

        localStorage.setItem("token", authToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("roleId", roleId);
        axiosHandler.defaults.headers["Authorization"] = `Bearer ${authToken}`;

        return {
          user,
          token: authToken,
          roleId,
          message,
          status: "success",
        };
      }
    } catch (error) {
      console.error("mfaLoginWithTotp API - Error:", error);
      return throwError(error, rejectWithValue);
    }
  }
);

export const mfaVerify = createAsyncThunk(
  "user/mfaVerify",
  async ({ corporateEmail, token }, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/mfa/verify`, {
        email: corporateEmail,
        totpCode: token,
      });

      if (response?.status === 200) {
        const user = response?.data?.data?.user;
        const authToken = response?.data?.data?.token;
        const roleId = response?.data?.data?.user?.userRoleId;
        const message = response?.data?.data?.message;

        localStorage.setItem("token", authToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("roleId", roleId);
        axiosHandler.defaults.headers["Authorization"] = `Bearer ${authToken}`;

        return {
          user,
          token: authToken,
          roleId,
          message,
        };
      }
    } catch (error) {
      console.error("mfaVerify API - Error:", error);
      return throwError(error, rejectWithValue);
    }
  }
);

export const resetMFA = createAsyncThunk(
  "user/resetMFA",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/reset-mfa/${userId}`);

      if (response?.status === 200) {
        return {
          userId,
          message: response?.data?.message || "MFA has been reset successfully",
          data: response?.data?.data,
        };
      }
    } catch (error) {
      console.error("resetMFA API - Error:", error);
      return throwError(error, rejectWithValue);
    }
  }
);

export const uploadMultipleEmployees = createAsyncThunk(
  "admin/uploadMultipleEmployees",
  async (employeeList, { rejectWithValue }) => {
    try {
      const incoming = employeeList;
      let usersArray = [];
      if (Array.isArray(incoming)) {
        usersArray = incoming;
      } else if (Array.isArray(incoming?.users)) {
        usersArray = incoming.users;
      }

      const payload = usersArray.map((u) => {
        const copy = { ...u };

        if (!copy.userRoleId && copy.role) {
          copy.userRoleId = copy.role;
          delete copy.role;
        }

        [
          "firstName",
          "middleName",
          "lastName",
          "corporateEmail",
          "businessUnit",
          "jobTitle",
        ].forEach((k) => {
          if (typeof copy[k] === "string") copy[k] = copy[k].trim();
        });
        return copy;
      });

      const response = await axiosHandler.post(
        `/admin/upload-multiple-employee`,
        payload // send RAW array as body
      );
      console.log(response.data);
      return response.data;
    } catch (err) {
      // Enhanced error handling
      if (err?.response?.data) {
        return rejectWithValue(err.response.data);
      }

      return rejectWithValue({
        status: "error",
        message: err?.message || "An unexpected error occurred",
      });
    }
  }
);

export const getPasswordRequirements = createAsyncThunk(
  "user/getPasswordRequirements",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.get(`/user/change-password`);
      if (response?.status === 200) {
        return response?.data?.data || [];
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);


  export const uploadBunchEmployee = createAsyncThunk(
    "admin/upload-employees-excel",
    async (  data , { rejectWithValue }) => {
      try {
        const response = await axiosHandler.post(`/admin/upload-employees-excel`, data);
  
        return response?.data;
      } catch (error) {
        return throwError(error, rejectWithValue);
      }
    }
  );
  

export const resendWelcomeEmail = createAsyncThunk(
  "user/resendWelcomeEmail",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosHandler.post(`/user/resend-welcome-email/${userId}`);
      if (response?.status === 200) {
        return response?.data;
      }
    } catch (error) {
      return throwError(error, rejectWithValue);
    }
  }
);

const throwError = (error, rejectWithValue) => {
  return rejectWithValue(error?.response?.data || "An error occurred");
};
